import React, { useEffect, useContext, useState, useCallback, useMemo } from 'react';
import { CircularProgress } from '@material-ui/core';
import clsx from 'clsx';
import { modeUIContext, setIsEditingForm, showSuccessNotification } from '@moderepo/mode-ui-state';
import { getAgentFeaturePermissions, RitzAppSettings } from '@moderepo/mode-internal-apis';
import { capitalizeAll, capitalizeFirst, GenericInfoComp } from '@moderepo/mode-app-components';
import { ritzDataContext, selectAppSettings, selectLoggedInAgent, selectProjectAgent, selectProjectById }
    from '@moderepo/mode-data-state-ritz';
import { fetchRobotCloudProjectSettings, rcDataContext, selectRobotCloudProjectSettings, updateRobotCloudProjectSettings }
    from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { RCProjectSettingsCompProps, RCProjectSettingsCompUpdatableInputs }
    from '../../components/robot-cloud/RCProjectSettingsComp';


interface RCProjectSettingErrors {
    projectApiKey?: string;
    tsdbSmartModuleId?: string;
    videoSmartModuleId?: string;
}


export interface RCProjectSettingsContainerProps {
    readonly className?: string | undefined;
    readonly projectId: number;
}

export const RCProjectSettingsContainer: React.FC<RCProjectSettingsContainerProps> = (props: RCProjectSettingsContainerProps) => {
    const { projectId } = props;

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState);
    const project = selectProjectById(dataState, projectId);
    const projectAgent = loggedInAgent ? selectProjectAgent(dataState, project?.id, loggedInAgent.id) : undefined;
    const appSettings = selectAppSettings(dataState) as RitzAppSettings;
    const permissions = getAgentFeaturePermissions(projectAgent);
    const rcProjectSettings = selectRobotCloudProjectSettings(rcDataState, projectId);
    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [rcProjectSettingInputErrors, setRCProjectSettingInputErrors] = useState<RCProjectSettingErrors>({ });


    useEffect(() => {
        if (!rcProjectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [rcProjectSettings, projectId, rcDataDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        // Clear errors
        setRCProjectSettingInputErrors({ });

        onChangeEditingMode(false);
    }, [onChangeEditingMode]);


    const onCancelEdit = useCallback(() => {
        closeEditing();
    }, [closeEditing]);



    /**
     * On project settings input change
     */
    const onRobotCloudProjectInfoFormInputChange = useCallback(() => {
        // do nothing
    }, []);


    /**
     * Handler for when the user click Save input from the Project Settings
     */
    const onRobotCloudProjectInfoFormSubmit = useCallback(async (inputs: RCProjectSettingsCompUpdatableInputs): Promise<boolean> => {

        if (!project || !rcProjectSettings) {
            return Promise.resolve(false);
        }

        const newInputErrors: RCProjectSettingErrors = { };

        const { projectApiKey, tsdbSmartModuleId, videoSmartModuleId } = inputs;
        if (!projectApiKey) {
            newInputErrors.projectApiKey = 'error.input.project_api_key.empty';
        }
        if (!tsdbSmartModuleId) {
            newInputErrors.tsdbSmartModuleId = 'error.input.tsdb_smart_module_id.empty';
        }
        if (!videoSmartModuleId) {
            newInputErrors.videoSmartModuleId = 'error.input.video_smart_module_id.empty';
        }


        // Update error state
        setRCProjectSettingInputErrors(newInputErrors);

        // check if there are errors by counting the number of errors fields in the newInputErrors object
        const hasErrors = Object.values(newInputErrors).length > 0;

        if (!hasErrors) {

            const settingChanged = rcProjectSettings?.projectApiKey !== projectApiKey        // Project API key changed
                || rcProjectSettings?.tsdbSmartModuleId !== tsdbSmartModuleId                // tsdbSmartModuleId changed
                || rcProjectSettings?.videoSmartModuleId !== videoSmartModuleId;             // video ModuleId changed


            // Only make API call if setting changed
            if (settingChanged) {

                try {
                    await rcDataDispatch(updateRobotCloudProjectSettings(project.id, {
                        projectApiKey,
                        tsdbSmartModuleId,
                        videoSmartModuleId,
                    }));
                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.project' }));

                    // API call success, return true and close editing
                    closeEditing();
                    return Promise.resolve(true);
                } catch (error) {
                    // API call failed, return FALSE
                    return Promise.resolve(false);
                }
            }

            // setting is the same, don't need to call API.
            closeEditing();
            return Promise.resolve(true);
        }

        // Input contains error, return false
        return Promise.resolve(false);

    }, [project, rcProjectSettings, closeEditing, rcDataDispatch, uiDispatch]);



    /**
     * Create InputSettings for the RobotCloud project settings component
     */
    const rcFieldsSettings = useMemo((): RCProjectSettingsCompProps['fields'] | undefined => {
        if (rcProjectSettings) {
            return {
                tsdbSmartModuleId: {
                    label       : capitalizeAll(trans('label.tsdb_smart_module_id')),
                    displayValue: rcProjectSettings.tsdbSmartModuleId || capitalizeAll(trans('label.none')),
                    value       : rcProjectSettings.tsdbSmartModuleId,
                    error       : rcProjectSettingInputErrors.tsdbSmartModuleId
                        ? capitalizeFirst(trans(rcProjectSettingInputErrors.tsdbSmartModuleId)) : undefined,
                    editable: true,
                },
                videoSmartModuleId: {
                    label       : capitalizeAll(trans('label.video_smart_module_id')),
                    displayValue: rcProjectSettings.videoSmartModuleId || capitalizeAll(trans('label.none')),
                    value       : rcProjectSettings.videoSmartModuleId,
                    error       : rcProjectSettingInputErrors.videoSmartModuleId
                        ? capitalizeFirst(trans(rcProjectSettingInputErrors.videoSmartModuleId)) : undefined,
                    editable: true,
                },
                projectApiKey: {
                    label       : capitalizeAll(trans('label.project_api_key')),
                    displayValue: rcProjectSettings.projectApiKey || capitalizeAll(trans('label.none')),
                    value       : rcProjectSettings.projectApiKey,
                    error       : rcProjectSettingInputErrors.projectApiKey
                        ? capitalizeFirst(trans(rcProjectSettingInputErrors.projectApiKey)) : undefined,
                    editable : true,
                    multiline: true,
                },
            };
        }

        return undefined;
    }, [trans, rcProjectSettings, rcProjectSettingInputErrors]);


    const fieldsGrouping = useMemo(() => {
        return [
            ['tsdbSmartModuleId', 'videoSmartModuleId'],
            ['projectApiKey'],
        ];
    }, []);



    return (
        <>
            {project && appSettings && rcFieldsSettings ? (
                <GenericInfoComp<RCProjectSettingsCompProps>
                    className={clsx(props.className, isEditing && 'editing')}
                    isEditing={isEditing}
                    title={capitalizeAll(trans('label.custom_project_settings'))}
                    icon="mode-icon rc-logo"
                    fields={rcFieldsSettings}
                    fieldsGrouping={fieldsGrouping}

                    actions={permissions.canUpdateProject ? {
                        startEdit: {
                            onClick: () => {
                                onChangeEditingMode(true);
                            },
                        },
                        cancelEdit: {
                            label  : capitalizeAll(trans('label.cancel')),
                            onClick: onCancelEdit,
                        },
                        saveUpdate: {
                            label  : capitalizeAll(trans('label.save')),
                            onClick: onRobotCloudProjectInfoFormSubmit,
                        },
                    } : undefined}
                    onFieldClicked={() => {
                        onChangeEditingMode(true);
                    }}
                    onFormInputChange={onRobotCloudProjectInfoFormInputChange}
                />
            ) : (
                <CircularProgress />
            )}
        </>
    );
};
